import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Bar, Pie, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement,
} from 'chart.js';
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  DollarSign, 
  Package, 
  Download, 
  Mail, 
  BarChart3, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Target,
  RefreshCw,
  UserCheck,
  ShoppingCart,
  FileText,
  Activity,
  Briefcase,
  User,
  Loader2,
  CreditCard,
  Wallet,
  Receipt,
  Calculator,
  MinusCircle,
  ChevronRight,
  Grid3X3,
  List,
  FileSpreadsheet,
  X
} from 'lucide-react';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  LineElement,
  PointElement
);

const API_BASE_URL = import.meta.env.VITE_API_URL ? 
  `${import.meta.env.VITE_API_URL}/api` : 
  (process.env.NODE_ENV === 'production' 
    ? 'https://www.ellasmgt.com/api'
    : 'http://localhost:5000/api');

// Helper function to get today's date in YYYY-MM-DD format
const getTodayString = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Helper function to get date N days ago
const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Quick date range presets
const DATE_PRESETS = {
  today: {
    label: 'Today',
    start: getTodayString(),
    end: getTodayString()
  },
  yesterday: {
    label: 'Yesterday',
    start: getDaysAgo(1),
    end: getDaysAgo(1)
  },
  last7days: {
    label: 'Last 7 Days',
    start: getDaysAgo(6),
    end: getTodayString()
  },
  last30days: {
    label: 'Last 30 Days',
    start: getDaysAgo(29),
    end: getTodayString()
  },
  thisMonth: {
  label: 'This Month',
  start: (() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}-01`;
  })(),
  end: getTodayString()
},
  lastMonth: {
  label: 'Last Month',
  start: (() => {
    const today = new Date();
    const y = today.getMonth() === 0 ? today.getFullYear() - 1 : today.getFullYear();
    const m = String(today.getMonth() === 0 ? 12 : today.getMonth()).padStart(2, '0');
    return `${y}-${m}-01`;
  })(),
  end: (() => {
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth(), 0);
    const y = lastDay.getFullYear();
    const m = String(lastDay.getMonth() + 1).padStart(2, '0');
    const d = String(lastDay.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  })()
}
};

// Skeleton Components for Loading State
const SkeletonKPICard = () => (
  <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="h-4 bg-gray-200 rounded w-24 mb-3"></div>
        <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
        <div className="h-3 bg-gray-100 rounded w-20"></div>
      </div>
      <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
    </div>
  </div>
);

const SkeletonChart = () => (
  <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-5 h-5 bg-gray-200 rounded"></div>
      <div className="h-5 bg-gray-200 rounded w-32"></div>
    </div>
    <div className="h-80 bg-gray-100 rounded-lg flex items-center justify-center">
      <div className="text-gray-300">
        <BarChart3 className="w-12 h-12" />
      </div>
    </div>
  </div>
);

const SkeletonTable = ({ rows = 5, cols = 3 }) => (
  <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
    <div className="flex items-center gap-2 mb-4">
      <div className="w-5 h-5 bg-gray-200 rounded"></div>
      <div className="h-5 bg-gray-200 rounded w-40"></div>
    </div>
    <div className="space-y-3">
      <div className="flex gap-4 border-b pb-2">
        {[...Array(cols)].map((_, i) => (
          <div key={i} className="h-4 bg-gray-200 rounded flex-1"></div>
        ))}
      </div>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="flex gap-4 py-2">
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="h-4 bg-gray-100 rounded flex-1"></div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const SkeletonLowStock = () => (
  <div className="bg-white rounded-xl shadow-sm border p-6 lg:col-span-2 animate-pulse">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="w-5 h-5 bg-gray-200 rounded"></div>
        <div className="h-5 bg-gray-200 rounded w-40"></div>
      </div>
      <div className="flex gap-2">
        <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
        <div className="w-8 h-8 bg-gray-200 rounded-lg"></div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-4 border">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
          <div className="h-3 bg-gray-100 rounded w-1/2 mb-3"></div>
          <div className="h-2 bg-gray-200 rounded-full w-full"></div>
        </div>
      ))}
    </div>
  </div>
);

function EnhancedReportsAnalytics() {
  // State management
  const [subBusiness, setSubBusiness] = useState('All');
  const [dateRange, setDateRange] = useState({ 
    start: getTodayString(),
    end: getTodayString() 
  });
  const [selectedPreset, setSelectedPreset] = useState('today');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dataLastUpdated, setDataLastUpdated] = useState(null);
  const [lowStockViewMode, setLowStockViewMode] = useState('grid');

  // VAT Report state
  const [showVATModal, setShowVATModal] = useState(false);
  const [vatMonth, setVatMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [vatSubBusiness, setVatSubBusiness] = useState('all');
  const [vatLoading, setVatLoading] = useState(false);
  const [vatData, setVatData] = useState(null);

  // Chart data states
  const [salesData, setSalesData] = useState({ labels: [], datasets: [] });
  const [inventoryData, setInventoryData] = useState({ labels: [], datasets: [] });
  const [attendanceData, setAttendanceData] = useState({ labels: [], datasets: [] });
  const [customerData, setCustomerData] = useState({ labels: [], datasets: [] });
  const [subBusinessData, setSubBusinessData] = useState({ labels: [], datasets: [] });
  const [expenseData, setExpenseData] = useState({ labels: [], datasets: [] });
  const [payrollData, setPayrollData] = useState({ labels: [], datasets: [] });
  
  // KPI and data states
  const [kpiMetrics, setKpiMetrics] = useState({
    totalRevenue: 0,
    totalExpenses: 0,
    netProfit: 0,
    activeStaff: 0,
    totalTransactions: 0,
    averageTransactionValue: 0,
    lowStockItems: 0,
    attendanceRate: 0,
    totalCustomers: 0,
    newCustomers: 0,
    inventoryCheckouts: 0,
    totalStaff: 0,
    totalPayrollCost: 0,
    totalDeductions: 0,
    totalNetPay: 0,
    payrollRecords: 0
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [staffPerformance, setStaffPerformance] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState([]);
  const [recentDeductions, setRecentDeductions] = useState([]);

  // Helper to get correct auth token
  const getAuthToken = () => {
    return localStorage.getItem('adminAuthToken') || localStorage.getItem('authToken');
  };

  // Get auth headers with token
  const getAuthHeaders = () => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Handle date preset selection
  const handlePresetSelect = (presetKey) => {
    const preset = DATE_PRESETS[presetKey];
    setDateRange({ start: preset.start, end: preset.end });
    setSelectedPreset(presetKey);
  };

  // Handle custom date range change
  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
    setSelectedPreset('custom');
  };

  // Fetch payroll data
  const fetchPayrollData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const currentPeriod = dateRange.start.substring(0, 7);
      
      const payrollResponse = await axios.post(`${API_BASE_URL}/payroll/generate-report`, {
        period: currentPeriod
      }, { headers });

      if (payrollResponse.data) {
        const payrollData = payrollResponse.data;
        
        setKpiMetrics(prev => ({
          ...prev,
          totalPayrollCost: payrollData.totalGrossPay || 0,
          totalDeductions: payrollData.totalDeductions || 0,
          totalNetPay: payrollData.totalNetPay || 0,
          payrollRecords: payrollData.totalStaff || 0
        }));

        setPayrollSummary(payrollData.payrollData?.slice(0, 10) || []);
        processPayrollChartData(payrollData.payrollData || []);
      }

      const deductionsResponse = await axios.get(`${API_BASE_URL}/payroll/deductions`, {
        params: { period: currentPeriod },
        headers
      });

      if (deductionsResponse.data?.deductionsData) {
        setRecentDeductions(deductionsResponse.data.deductionsData.slice(0, 10));
      }

    } catch (err) {
      console.warn('Payroll data not available:', err.message);
    }
  }, [dateRange]);

  // Process payroll chart data
  const processPayrollChartData = (payrollRecords) => {
    if (!payrollRecords || !payrollRecords.length) {
      setPayrollData({
        labels: ['No Data'],
        datasets: [{
          label: 'Payroll Costs (₦)',
          data: [0],
          backgroundColor: 'rgba(168, 85, 247, 0.8)'
        }]
      });
      return;
    }

    const departments = {};
    payrollRecords.forEach(record => {
      const dept = record.department || 'General';
      if (!departments[dept]) {
        departments[dept] = { grossPay: 0, deductions: 0, netPay: 0 };
      }
      departments[dept].grossPay += record.grossPay || 0;
      departments[dept].deductions += record.totalDeductions || 0;
      departments[dept].netPay += record.netPay || 0;
    });

    setPayrollData({
      labels: Object.keys(departments),
      datasets: [
        {
          label: 'Gross Pay (₦)',
          data: Object.values(departments).map(d => d.grossPay),
          backgroundColor: 'rgba(34, 197, 94, 0.8)',
          borderRadius: 6
        },
        {
          label: 'Deductions (₦)',
          data: Object.values(departments).map(d => d.deductions),
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderRadius: 6
        },
        {
          label: 'Net Pay (₦)',
          data: Object.values(departments).map(d => d.netPay),
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderRadius: 6
        }
      ]
    });
  };

  // Fetch all data using the centralized dashboard endpoint
  const fetchReportsData = useCallback(async () => {
    setLoading(true);
    setError('');
    
    try {
      const params = { 
        startDate: dateRange.start, 
        endDate: dateRange.end 
      };
      
      if (subBusiness !== 'All') {
        params.subBusiness = subBusiness;
      }

      const headers = getAuthHeaders();
      const response = await axios.get(`${API_BASE_URL}/reports/dashboard-data`, { 
        params,
        headers 
      });

      if (response.data.success) {
        const dashboardData = response.data.data;
        
        setKpiMetrics(prev => ({
          ...prev,
          ...dashboardData.kpiMetrics
        }));
        
        processSalesDataFromAPI(dashboardData.chartData.salesByDate);
        processInventoryDataFromAPI(dashboardData.tableData.topSellingItems);
        processAttendanceDataFromAPI(dashboardData.chartData.attendanceByDate);
        processCustomerDataFromAPI(dashboardData.chartData.customersByType);
        processExpenseDataFromAPI(dashboardData.chartData.expensesByCategory);
        processSubBusinessDataFromAPI(dashboardData.chartData.revenueByBusiness);
        
        setTopSellingItems(dashboardData.tableData.topSellingItems || []);
        setTopCustomers(dashboardData.tableData.topCustomers || []);
        setLowStockItems(dashboardData.tableData.lowStockItems || []);
        setRecentTransactions(dashboardData.tableData.recentTransactions || []);
        setStaffPerformance(dashboardData.tableData.staffPerformance || []);
        setRecentActivities(dashboardData.recentActivities || []);

        setDataLastUpdated(new Date());
      } else {
        throw new Error(response.data.message || 'Failed to fetch dashboard data');
      }

      await fetchPayrollData();

    } catch (err) {
      console.error('Error fetching reports data:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 404) {
        setError('Dashboard data endpoint not available. Please contact system administrator.');
      } else {
        setError(`Failed to fetch dashboard data: ${err.message}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  }, [subBusiness, dateRange, fetchPayrollData]);

  // Process sales data from API response
  const processSalesDataFromAPI = (salesByDate) => {
    if (!salesByDate || Object.keys(salesByDate).length === 0) {
      setSalesData({
        labels: ['No Data'],
        datasets: [{
          label: 'Daily Sales (₦)',
          data: [0],
          backgroundColor: 'rgba(59, 130, 246, 0.8)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 2,
          fill: false
        }]
      });
      return;
    }

    const sortedDates = Object.keys(salesByDate).sort((a, b) => new Date(a) - new Date(b));

    setSalesData({
      labels: sortedDates,
      datasets: [{
        label: 'Daily Sales (₦)',
        data: sortedDates.map(date => salesByDate[date]),
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderColor: 'rgb(59, 130, 246)',
        borderWidth: 3,
        fill: true,
        tension: 0.4
      }]
    });
  };

  // Process inventory data from API response
  const processInventoryDataFromAPI = (topItems) => {
    if (!topItems || !topItems.length) {
      setInventoryData({
        labels: ['No Data'],
        datasets: [{
          label: 'Stock Levels',
          data: [0],
          backgroundColor: ['rgba(156, 163, 175, 0.8)']
        }]
      });
      return;
    }

    const displayItems = topItems.slice(0, 10);

    setInventoryData({
      labels: displayItems.map(item => item.name || 'Unknown'),
      datasets: [{
        label: 'Sales Quantity',
        data: displayItems.map(item => item.quantity || 0),
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(34, 197, 94, 0.8)',
          'rgba(168, 85, 247, 0.8)',
          'rgba(251, 146, 60, 0.8)',
          'rgba(14, 165, 233, 0.8)'
        ],
        borderWidth: 0,
        borderRadius: 8
      }]
    });
  };

  // Process attendance data from API response
  const processAttendanceDataFromAPI = (attendanceByDate) => {
    if (!attendanceByDate || Object.keys(attendanceByDate).length === 0) {
      setAttendanceData({
        labels: ['No Data'],
        datasets: [{
          label: 'Staff Present',
          data: [0],
          backgroundColor: 'rgba(139, 92, 246, 0.8)'
        }]
      });
      return;
    }

    const sortedDates = Object.keys(attendanceByDate).sort((a, b) => new Date(a) - new Date(b));

    setAttendanceData({
      labels: sortedDates,
      datasets: [
        {
          label: 'Present',
          data: sortedDates.map(date => attendanceByDate[date].present),
          backgroundColor: 'rgba(16, 185, 129, 0.8)',
          borderRadius: 6
        },
        {
          label: 'Absent',
          data: sortedDates.map(date => attendanceByDate[date].absent),
          backgroundColor: 'rgba(239, 68, 68, 0.8)',
          borderRadius: 6
        },
        {
          label: 'Late',
          data: sortedDates.map(date => attendanceByDate[date].late),
          backgroundColor: 'rgba(245, 158, 11, 0.8)',
          borderRadius: 6
        }
      ]
    });
  };

  // Process customer data from API response
  const processCustomerDataFromAPI = (customersByType) => {
    if (!customersByType || Object.keys(customersByType).length === 0) {
      setCustomerData({
        labels: ['No Data'],
        datasets: [{
          label: 'Customer Types',
          data: [0],
          backgroundColor: ['rgba(156, 163, 175, 0.8)']
        }]
      });
      return;
    }

    setCustomerData({
      labels: Object.keys(customersByType),
      datasets: [{
        label: 'Customer Types',
        data: Object.values(customersByType),
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)'
        ]
      }]
    });
  };

  // Process expense data from API response
  const processExpenseDataFromAPI = (expensesByCategory) => {
    if (!expensesByCategory || Object.keys(expensesByCategory).length === 0) {
      setExpenseData({
        labels: ['No Data'],
        datasets: [{
          label: 'Expenses by Category (₦)',
          data: [0],
          backgroundColor: ['rgba(156, 163, 175, 0.8)']
        }]
      });
      return;
    }

    setExpenseData({
      labels: Object.keys(expensesByCategory),
      datasets: [{
        label: 'Expenses by Category (₦)',
        data: Object.values(expensesByCategory),
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(139, 92, 246, 0.8)',
          'rgba(236, 72, 153, 0.8)',
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)'
        ]
      }]
    });
  };

  // Process sub-business data from API response
  const processSubBusinessDataFromAPI = (revenueByBusiness) => {
    if (!revenueByBusiness || Object.keys(revenueByBusiness).length === 0) {
      setSubBusinessData({
        labels: ['No Data'],
        datasets: [{
          label: 'Revenue by Sub-Business (₦)',
          data: [0],
          backgroundColor: ['rgba(156, 163, 175, 0.8)']
        }]
      });
      return;
    }

    setSubBusinessData({
      labels: Object.keys(revenueByBusiness),
      datasets: [{
        label: 'Revenue by Sub-Business (₦)',
        data: Object.values(revenueByBusiness),
        backgroundColor: [
          'rgba(239, 68, 68, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(139, 92, 246, 0.8)'
        ]
      }]
    });
  };

  // Export report
  const exportReport = useCallback(() => {
    const reportData = {
      dateRange,
      subBusiness,
      kpiMetrics,
      lowStockItems: lowStockItems.map(item => ({
        name: item.name,
        quantity: item.quantity,
        reorderPoint: item.reorderPoint
      })),
      topSellingItems,
      topCustomers: topCustomers.map(customer => ({
        name: customer.customer?.name || 'Unknown',
        totalSpent: customer.totalSpent,
        transactions: customer.transactions
      })),
      payrollSummary: payrollSummary.map(record => ({
        employeeName: record.employeeName,
        netPay: record.netPay,
        totalDeductions: record.totalDeductions
      })),
      exportedAt: new Date().toISOString()
    };
    
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ellas-business-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setSuccess('Report exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  }, [dateRange, subBusiness, kpiMetrics, lowStockItems, topSellingItems, topCustomers, payrollSummary]);

  // Generate daily report
  const handleGenerateDailyReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/reports/daily`, {
        subBusiness: subBusiness !== 'All' ? subBusiness : undefined,
        startDate: dateRange.start,
        endDate: dateRange.end
      }, {
        headers: getAuthHeaders()
      });

      setSuccess('Daily business report generated and sent via email successfully!');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Authentication failed. Please login again.');
      } else if (err.response?.status === 404) {
        setError('Report generation endpoint not available. Feature may not be implemented.');
      } else {
        setError('Failed to generate daily report: ' + (err.response?.data?.message || err.message));
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  }, [subBusiness, dateRange]);

  // Load data on component mount and when filters change
  useEffect(() => {
    fetchReportsData();
  }, [fetchReportsData]);

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: { family: 'system-ui', size: 12 },
          color: '#1F2937',
          usePointStyle: true,
          padding: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#3B82F6',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || context.parsed;
            if (typeof value === 'number' && value > 1000) {
              return `${label}: ₦${value.toLocaleString()}`;
            }
            return `${label}: ${value}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: {
          font: { family: 'system-ui', size: 10 },
          color: '#6B7280'
        }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: { family: 'system-ui', size: 11 },
          color: '#1F2937',
          usePointStyle: true,
          padding: 15
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#3B82F6',
        borderWidth: 1,
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((value / total) * 100).toFixed(1);
            if (typeof value === 'number' && value > 1000) {
              return `${label}: ₦${value.toLocaleString()} (${percentage}%)`;
            }
            return `${label}: ${value} (${percentage}%)`;
          }
        }
      }
    }
  };

  // ── VAT Report ──────────────────────────────────────────────────────────────
  const fmt = (n) => `₦${Number(n || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const fetchVATReport = async () => {
    setVatLoading(true);
    setVatData(null);
    try {
      const params = new URLSearchParams({ month: vatMonth });
      if (vatSubBusiness !== 'all') params.append('subBusiness', vatSubBusiness);
      const res = await axios.get(`${API_BASE_URL}/reports/vat-report?${params}`, { headers: getAuthHeaders() });
      setVatData(res.data);
    } catch (e) {
      alert('Failed to load VAT report: ' + (e.response?.data?.message || e.message));
    } finally {
      setVatLoading(false);
    }
  };

  const exportVATExcel = () => {
    if (!vatData) return;
    import('xlsx').then(XLSX => {
      const rows = vatData.transactions.map(t => ({
        'No': t.no,
        'Date': new Date(t.date).toLocaleString('en-GB'),
        'Description': t.description,
        'Category': t.category,
        'Sub-Business': t.subBusiness,
        'Customer': t.customer,
        'Payment Method': t.paymentMethod,
        'Net Amount (₦)': t.subtotal,
        'VAT (₦)': t.vat,
        'Total (₦)': t.totalAmount,
      }));
      rows.push({});
      rows.push({ 'No': 'SUMMARY', 'Description': '', 'Net Amount (₦)': vatData.summary.totalNet, 'VAT (₦)': vatData.summary.totalVAT, 'Total (₦)': vatData.summary.totalGross });
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 5 }, { wch: 20 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'VAT Report');
      XLSX.writeFile(wb, `VAT_Report_${vatMonth}.xlsx`);
    });
  };

  const exportVATPDF = () => {
    if (!vatData) return;
    import('jspdf').then(({ default: jsPDF }) =>
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(16);
        doc.text(`VAT Report — ${vatData.month}`, 14, 15);
        doc.setFontSize(10);
        doc.text(`Transactions: ${vatData.summary.transactionCount}   Net: ${fmt(vatData.summary.totalNet)}   VAT: ${fmt(vatData.summary.totalVAT)}   Gross: ${fmt(vatData.summary.totalGross)}`, 14, 23);

        doc.autoTable({
          startY: 28,
          head: [['No', 'Date', 'Description', 'Category', 'Sub-Biz', 'Customer', 'Payment', 'Net (₦)', 'VAT (₦)', 'Total (₦)']],
          body: vatData.transactions.map(t => [
            t.no,
            new Date(t.date).toLocaleDateString('en-GB'),
            t.description,
            t.category,
            t.subBusiness,
            t.customer,
            t.paymentMethod,
            Number(t.subtotal).toFixed(2),
            Number(t.vat).toFixed(2),
            Number(t.totalAmount).toFixed(2),
          ]),
          foot: [['', '', '', '', '', '', 'TOTAL', Number(vatData.summary.totalNet).toFixed(2), Number(vatData.summary.totalVAT).toFixed(2), Number(vatData.summary.totalGross).toFixed(2)]],
          styles: { fontSize: 8 },
          headStyles: { fillColor: [79, 70, 229] },
          footStyles: { fillColor: [243, 244, 246], textColor: [17, 24, 39], fontStyle: 'bold' },
        });

        doc.save(`VAT_Report_${vatMonth}.pdf`);
      })
    );
  };
  // ────────────────────────────────────────────────────────────────────────────

  // Low Stock Items Grid Component
  const LowStockGrid = () => {
    const displayItems = lowStockItems.slice(0, 12);
    const hasMore = lowStockItems.length > 12;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Low Stock Items ({lowStockItems.length})
            </h3>
            {hasMore && (
              <span className="text-sm text-gray-500">
                Showing 12 of {lowStockItems.length}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setLowStockViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                lowStockViewMode === 'grid' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setLowStockViewMode('table')}
              className={`p-2 rounded-lg transition-colors ${
                lowStockViewMode === 'table' 
                  ? 'bg-blue-100 text-blue-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {lowStockViewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {displayItems.length > 0 ? displayItems.map((item, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-lg p-4 hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate" title={item.name}>
                      {item.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">{item.subBusiness || 'General'}</p>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      {item.quantity === 0 ? 'Out' : 'Low'}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Current:</span>
                    <span className={`font-bold ${item.quantity === 0 ? 'text-red-600' : 'text-orange-600'}`}>
                      {item.quantity || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Reorder at:</span>
                    <span className="text-gray-700">{item.reorderPoint || 10}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        item.quantity === 0 ? 'bg-red-500' : 'bg-orange-400'
                      }`}
                      style={{ 
                        width: `${Math.min(100, ((item.quantity || 0) / (item.reorderPoint || 10)) * 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No low stock items</p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium text-gray-700">Item Name</th>
                  <th className="text-right py-2 font-medium text-gray-700">Stock</th>
                  <th className="text-right py-2 font-medium text-gray-700">Reorder Point</th>
                  <th className="text-left py-2 font-medium text-gray-700">Sub-Business</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.length > 0 ? displayItems.map((item, index) => (
                  <tr key={index} className="border-b hover:bg-gray-50">
                    <td className="py-2 font-medium">{item.name}</td>
                    
                    <td className="text-right py-2 text-red-600 font-bold">{item.quantity || 0}</td>
                    <td className="text-right py-2 text-gray-600">{item.reorderPoint || 10}</td>
                    <td className="py-2 text-gray-600">{item.subBusiness || 'General'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="text-center py-4 text-gray-500">No low stock items</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {hasMore && (
          <div className="text-center pt-4 border-t">
            <button className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1 mx-auto transition-colors">
              View All {lowStockItems.length} Items
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Business Analytics Dashboard</h1>
          <p className="text-gray-600">Comprehensive insights and reports for your business operations</p>
          {dataLastUpdated && (
            <p className="text-sm text-gray-500 mt-2">
              Last updated: {dataLastUpdated.toLocaleString()}
            </p>
          )}
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            {success}
          </div>
        )}

        {/* Enhanced Controls */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Date Range Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Quick Date Ranges</label>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(DATE_PRESETS).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => handlePresetSelect(key)}
                    className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                      selectedPreset === key
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Custom Date Range</label>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => handleDateRangeChange('start', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => handleDateRangeChange('end', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Actions */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-6 pt-6 border-t">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sub-Business</label>
              <select
                value={subBusiness}
                onChange={(e) => setSubBusiness(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="All">All Businesses</option>
                <option value="Salon">Salon</option>
                <option value="Gym">Gym</option>
                <option value="Spa">Spa</option>
              </select>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={fetchReportsData}
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={exportReport}
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
            
            <div className="flex items-end">
              <button
                onClick={handleGenerateDailyReport}
                disabled={loading}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Email Report
              </button>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => setShowVATModal(true)}
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" />
                VAT Report
              </button>
            </div>

            <div className="flex items-end">
              <div className="w-full text-center">
                <div className="text-xs text-gray-500">Period</div>
                <div className="text-sm font-medium text-gray-700">
                  {dateRange.start === dateRange.end ? 
                    new Date(dateRange.start).toLocaleDateString() :
                    `${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()}`
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced KPI Cards with Payroll */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            <>
              {[...Array(8)].map((_, i) => <SkeletonKPICard key={i} />)}
            </>
          ) : (
            <>
          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600">
                  ₦{kpiMetrics.totalRevenue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">{kpiMetrics.totalTransactions} transactions</p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Net Profit</p>
                <p className={`text-2xl font-bold ${kpiMetrics.netProfit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  ₦{kpiMetrics.netProfit.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Expenses: ₦{kpiMetrics.totalExpenses.toLocaleString()}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Staff Attendance</p>
                <p className={`text-2xl font-bold ${kpiMetrics.attendanceRate >= 90 ? 'text-green-600' : kpiMetrics.attendanceRate >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {kpiMetrics.attendanceRate.toFixed(1)}%
                </p>
                <p className="text-xs text-gray-500 mt-1">{kpiMetrics.activeStaff} of {kpiMetrics.totalStaff} present</p>
              </div>
              <Users className="w-8 h-8 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Low Stock Items</p>
                <p className={`text-2xl font-bold ${kpiMetrics.lowStockItems <= 2 ? 'text-green-600' : kpiMetrics.lowStockItems <= 5 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {kpiMetrics.lowStockItems}
                </p>
                <p className="text-xs text-gray-500 mt-1">Needs attention</p>
              </div>
              <Package className="w-8 h-8 text-red-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Average Transaction</p>
                <p className="text-2xl font-bold text-purple-600">
                  ₦{kpiMetrics.averageTransactionValue.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Per transaction</p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Customers</p>
                <p className="text-2xl font-bold text-pink-600">{kpiMetrics.totalCustomers}</p>
                <p className="text-xs text-gray-500 mt-1">{kpiMetrics.newCustomers} new this period</p>
              </div>
              <UserCheck className="w-8 h-8 text-pink-500" />
            </div>
          </div>

          {/* New Payroll KPI Cards */}
          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Payroll Cost</p>
                <p className="text-2xl font-bold text-indigo-600">
                  ₦{kpiMetrics.totalPayrollCost.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">{kpiMetrics.payrollRecords} employees</p>
              </div>
              <Wallet className="w-8 h-8 text-indigo-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-medium">Total Deductions</p>
                <p className="text-2xl font-bold text-orange-600">
                  ₦{kpiMetrics.totalDeductions.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500 mt-1">Penalties & deductions</p>
              </div>
              <MinusCircle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
            </>
          )}
        </div>

        {/* Enhanced Charts Grid with Payroll */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {[...Array(6)].map((_, i) => <SkeletonChart key={i} />)}
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales Trend */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Sales Trend
            </h3>
            <div className="h-80">
              <Line data={salesData} options={chartOptions} />
            </div>
          </div>

          {/* Revenue by Sub-Business */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Revenue by Sub-Business
            </h3>
            <div className="h-80">
              <Doughnut data={subBusinessData} options={pieChartOptions} />
            </div>
          </div>

          {/* Top Selling Items */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5 text-purple-500" />
              Top Selling Items
            </h3>
            <div className="h-80">
              <Bar data={inventoryData} options={chartOptions} />
            </div>
          </div>

          {/* Attendance Overview */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Attendance Overview
            </h3>
            <div className="h-80">
              <Bar data={attendanceData} options={chartOptions} />
            </div>
          </div>

          {/* Payroll by Department */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-indigo-500" />
              Payroll by Department
            </h3>
            <div className="h-80">
              <Bar data={payrollData} options={chartOptions} />
            </div>
          </div>

          {/* Customer Distribution */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-pink-500" />
              Customer Distribution
            </h3>
            <div className="h-80">
              <Pie data={customerData} options={pieChartOptions} />
            </div>
          </div>
        </div>
        )}

        {/* Enhanced Data Tables Section */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SkeletonLowStock />
            <SkeletonTable rows={5} cols={3} />
            <SkeletonTable rows={5} cols={3} />
            <SkeletonTable rows={5} cols={4} />
            <SkeletonTable rows={5} cols={3} />
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Low Stock Items with Enhanced Grid */}
          <div className="bg-white rounded-xl shadow-sm border p-6 lg:col-span-2">
            <LowStockGrid />
          </div>

          {/* Top Selling Items */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" />
              Top Selling Items
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-gray-700">Item Name</th>
                    <th className="text-right py-2 font-medium text-gray-700">Quantity Sold</th>
                    <th className="text-right py-2 font-medium text-gray-700">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {topSellingItems.length > 0 ? topSellingItems.map((item, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{item.name}</td>
                      <td className="text-right py-2 text-blue-600 font-bold">{item.quantity}</td>
                      <td className="text-right py-2 text-green-600 font-bold">₦{item.revenue.toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-gray-500">No sales data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Top Customers */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-500" />
              Top Customers
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-gray-700">Customer Name</th>
                    <th className="text-right py-2 font-medium text-gray-700">Total Spent</th>
                    <th className="text-right py-2 font-medium text-gray-700">Transactions</th>
                  </tr>
                </thead>
                <tbody>
                  {topCustomers.length > 0 ? topCustomers.map((customer, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{customer.customer?.name || 'Unknown Customer'}</td>
                      <td className="text-right py-2 text-green-600 font-bold">₦{customer.totalSpent.toLocaleString()}</td>
                      <td className="text-right py-2 text-blue-600 font-bold">{customer.transactions}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-gray-500">No customer data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Staff Performance */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Staff Performance
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-gray-700">Staff Name</th>
                    <th className="text-right py-2 font-medium text-gray-700">Attendance Rate</th>
                    <th className="text-right py-2 font-medium text-gray-700">Present Days</th>
                    <th className="text-right py-2 font-medium text-gray-700">Late Days</th>
                  </tr>
                </thead>
                <tbody>
                  {staffPerformance.length > 0 ? staffPerformance.map((staff, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{staff.name}</td>
                      <td className={`text-right py-2 font-bold ${staff.attendanceRate >= 90 ? 'text-green-600' : staff.attendanceRate >= 75 ? 'text-yellow-600' : 'text-red-600'}`}>
                        {staff.attendanceRate.toFixed(1)}%
                      </td>
                      <td className="text-right py-2 text-blue-600">{staff.present}</td>
                      <td className="text-right py-2 text-orange-600">{staff.late}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="4" className="text-center py-4 text-gray-500">No staff performance data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Payroll Summary */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-purple-500" />
              Payroll Summary
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 font-medium text-gray-700">Employee</th>
                    <th className="text-right py-2 font-medium text-gray-700">Net Pay</th>
                    <th className="text-right py-2 font-medium text-gray-700">Deductions</th>
                  </tr>
                </thead>
                <tbody>
                  {payrollSummary.length > 0 ? payrollSummary.map((record, index) => (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-medium">{record.employeeName || `${record.employeeNumber}`}</td>
                      <td className="text-right py-2 text-green-600 font-bold">₦{(record.netPay || 0).toLocaleString()}</td>
                      <td className="text-right py-2 text-red-600 font-bold">₦{(record.totalDeductions || 0).toLocaleString()}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="3" className="text-center py-4 text-gray-500">No payroll data available</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        )}

        {/* Recent Transactions */}
        {loading ? (
          <SkeletonTable rows={5} cols={6} />
        ) : (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-green-500" />
            Recent Transactions
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 font-medium text-gray-700">Date & Time</th>
                  <th className="text-left py-2 font-medium text-gray-700">Customer</th>
                  <th className="text-right py-2 font-medium text-gray-700">Amount</th>
                  <th className="text-left py-2 font-medium text-gray-700">Sub-Business</th>
                  <th className="text-left py-2 font-medium text-gray-700">Payment Method</th>
                  <th className="text-right py-2 font-medium text-gray-700">Items</th>
                </tr>
              </thead>
              <tbody>
                {recentTransactions.length > 0 ? recentTransactions.map((transaction, index) => {
                  // ✅ Handle array format for subBusiness
                  const subBusinessArray = Array.isArray(transaction.subBusiness)
                    ? transaction.subBusiness
                    : [transaction.subBusiness];
                  const displaySubBusiness = subBusinessArray.length === 1
                    ? subBusinessArray[0] || 'General'
                    : subBusinessArray.join(' & ');

                  return (
                    <tr key={index} className="border-b hover:bg-gray-50">
                      <td className="py-2 text-gray-600">
                        {new Date(transaction.createdAt).toLocaleString()}
                      </td>
                      <td className="py-2 font-medium">{transaction.customer?.name || 'Walk-in Customer'}</td>
                      <td className="text-right py-2 text-green-600 font-bold">₦{(transaction.totalAmount || 0).toLocaleString()}</td>
                      <td className="py-2 text-gray-600">{displaySubBusiness}</td>
                      <td className="py-2 text-gray-600">{(transaction.paymentMethod || 'cash').toUpperCase()}</td>
                      <td className="text-right py-2 text-blue-600">{transaction.items?.length || 0}</td>
                    </tr>
                  );
                }) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4 text-gray-500">No recent transactions</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}

        {/* Recent Activities */}
        {loading ? (
          <SkeletonTable rows={4} cols={3} />
        ) : (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-500" />
            Recent Activities
          </h3>
          <div className="space-y-3">
            {recentActivities.length > 0 ? recentActivities.map((activity, index) => (
              <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                <div className={`p-2 rounded-full bg-white ${activity.color}`}>
                  {activity.icon === 'ShoppingCart' && <ShoppingCart className="w-4 h-4" />}
                  {activity.icon === 'UserCheck' && <UserCheck className="w-4 h-4" />}
                  {activity.icon === 'Package' && <Package className="w-4 h-4" />}
                  {activity.icon === 'FileText' && <FileText className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{activity.title}</div>
                  <div className="text-sm text-gray-600">{activity.description}</div>
                </div>
                <div className="text-xs text-gray-500">{activity.time}</div>
              </div>
            )) : (
              <div className="text-center py-8 text-gray-500">
                <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No recent activities to display</p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Enhanced Summary Statistics with Payroll */}
        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border p-6 animate-pulse">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-5 h-5 bg-gray-200 rounded"></div>
              <div className="h-5 bg-gray-200 rounded w-40"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="text-center">
                  <div className="h-10 bg-gray-200 rounded w-24 mx-auto mb-2"></div>
                  <div className="h-4 bg-gray-100 rounded w-32 mx-auto"></div>
                </div>
              ))}
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-purple-500" />
            Summary Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600 mb-2">
                {kpiMetrics.totalTransactions}
              </div>
              <div className="text-gray-600">Total Transactions</div>
              <div className="text-sm text-gray-500 mt-1">
                Avg: ₦{kpiMetrics.averageTransactionValue.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 mb-2">
                ₦{kpiMetrics.totalRevenue.toLocaleString()}
              </div>
              <div className="text-gray-600">Total Revenue</div>
              <div className="text-sm text-gray-500 mt-1">
                Profit: ₦{kpiMetrics.netProfit.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 mb-2">
                {kpiMetrics.attendanceRate.toFixed(1)}%
              </div>
              <div className="text-gray-600">Attendance Rate</div>
              <div className="text-sm text-gray-500 mt-1">
                {kpiMetrics.activeStaff} of {kpiMetrics.totalStaff} staff
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-indigo-600 mb-2">
                ₦{kpiMetrics.totalPayrollCost.toLocaleString()}
              </div>
              <div className="text-gray-600">Payroll Cost</div>
              <div className="text-sm text-gray-500 mt-1">
                Deductions: ₦{kpiMetrics.totalDeductions.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Footer */}
        <div className="text-center py-6 text-gray-500 text-sm">
          <p>Business Analytics Dashboard • Last updated: {new Date().toLocaleString()}</p>
          <p className="mt-1">
            Period: {dateRange.start === dateRange.end ?
              new Date(dateRange.start).toLocaleDateString() :
              `${new Date(dateRange.start).toLocaleDateString()} to ${new Date(dateRange.end).toLocaleDateString()}`
            } • Sub-Business: {subBusiness}
          </p>
        </div>
      </div>

      {/* ── VAT Report Modal ── */}
      {showVATModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div className="flex items-center gap-3">
                <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                <h2 className="text-lg font-bold text-gray-900">VAT Report</h2>
              </div>
              <button onClick={() => setShowVATModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>

            {/* Controls */}
            <div className="px-6 py-4 border-b bg-gray-50 flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                <input
                  type="month"
                  value={vatMonth}
                  min="2026-01"
                  onChange={e => setVatMonth(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sub-Business</label>
                <select
                  value={vatSubBusiness}
                  onChange={e => setVatSubBusiness(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="all">All</option>
                  <option value="Salon">Salon</option>
                  <option value="Gym">Gym</option>
                  <option value="Spa">Spa</option>
                </select>
              </div>
              <button
                onClick={fetchVATReport}
                disabled={vatLoading}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                {vatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                {vatLoading ? 'Loading...' : 'Generate'}
              </button>
              {vatData && (
                <>
                  <button onClick={exportVATExcel} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                    <Download className="w-4 h-4" /> Excel
                  </button>
                  <button onClick={exportVATPDF} className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                    <Download className="w-4 h-4" /> PDF
                  </button>
                </>
              )}
            </div>

            {/* Summary bar */}
            {vatData && (
              <div className="px-6 py-3 bg-indigo-50 border-b flex flex-wrap gap-6 text-sm">
                <span className="text-gray-600">Period: <strong className="text-gray-900">{vatData.month}</strong></span>
                <span className="text-gray-600">Transactions: <strong className="text-gray-900">{vatData.summary.transactionCount}</strong></span>
                <span className="text-gray-600">Net Sales: <strong className="text-gray-900">{fmt(vatData.summary.totalNet)}</strong></span>
                <span className="text-indigo-700 font-semibold">VAT Collected: <strong>{fmt(vatData.summary.totalVAT)}</strong></span>
                <span className="text-gray-600">Gross Total: <strong className="text-gray-900">{fmt(vatData.summary.totalGross)}</strong></span>
              </div>
            )}

            {/* Table */}
            <div className="flex-1 overflow-auto px-6 py-4">
              {!vatData && !vatLoading && (
                <div className="text-center py-16 text-gray-400">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p>Select a month and click Generate</p>
                </div>
              )}
              {vatData && vatData.transactions.length === 0 && (
                <div className="text-center py-16 text-gray-400">No VAT transactions found for {vatData.month}</div>
              )}
              {vatData && vatData.transactions.length > 0 && (
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="bg-gray-100 text-xs text-gray-600 uppercase">
                      <th className="text-left px-3 py-2">#</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Description</th>
                      <th className="text-left px-3 py-2">Category</th>
                      <th className="text-left px-3 py-2">Sub-Biz</th>
                      <th className="text-left px-3 py-2">Customer</th>
                      <th className="text-left px-3 py-2">Payment</th>
                      <th className="text-right px-3 py-2">Net (₦)</th>
                      <th className="text-right px-3 py-2">VAT (₦)</th>
                      <th className="text-right px-3 py-2">Total (₦)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vatData.transactions.map(t => (
                      <tr key={t.no} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{t.no}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{new Date(t.date).toLocaleDateString('en-GB')}</td>
                        <td className="px-3 py-2 max-w-[160px] truncate">{t.description}</td>
                        <td className="px-3 py-2">{t.category}</td>
                        <td className="px-3 py-2">{t.subBusiness}</td>
                        <td className="px-3 py-2">{t.customer}</td>
                        <td className="px-3 py-2 capitalize">{t.paymentMethod}</td>
                        <td className="px-3 py-2 text-right">{Number(t.subtotal).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-right text-indigo-700 font-medium">{Number(t.vat).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-right font-semibold">{Number(t.totalAmount).toLocaleString('en-NG', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-indigo-50 font-bold text-sm">
                      <td colSpan={7} className="px-3 py-3 text-right text-gray-700">TOTAL</td>
                      <td className="px-3 py-3 text-right">{fmt(vatData.summary.totalNet)}</td>
                      <td className="px-3 py-3 text-right text-indigo-700">{fmt(vatData.summary.totalVAT)}</td>
                      <td className="px-3 py-3 text-right">{fmt(vatData.summary.totalGross)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EnhancedReportsAnalytics;